import { useState, useEffect } from 'react';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import { useTranslation } from '../../i18n/hooks/useTranslation';

/**
 * ListBuilderInput Component
 * Renders a dynamic list builder for Increment and BulletList type placeholders
 *
 * @param {string} label - Title of the list
 * @param {string} description - Predefined items in format: (Item 1) (Item 2) (Item 3)
 * @param {string} listType - 'Increment' for numbered list, 'BulletList' for bullet points
 * @param {string} language - Template language ('english', 'bangla', 'mixed')
 * @param {object} value - Current value { items: [], listType: string }
 * @param {function} onChange - Callback when value changes
 */
export default function ListBuilderInput({ label, description, listType = 'Increment', language = 'bangla', value, onChange, onFocus }) {
    const { t } = useTranslation();

    // Parse predefined items from description using () format
    const parsePredefinedItems = (desc) => {
        if (!desc) return [];
        const matches = desc.match(/\(([^)]+)\)/g);
        if (!matches) return [];
        return matches.map(m => m.slice(1, -1).trim()).filter(item => item.length > 0);
    };

    // Convert number to Bangla numeral
    const toBanglaNumber = (num) => {
        const banglaDigits = ['০', '১', '২', '৩', '৪', '৫', '৬', '৭', '৮', '৯'];
        return num.toString().split('').map(d => banglaDigits[parseInt(d)] || d).join('');
    };

    // Get list marker based on type and index
    const getMarker = (index) => {
        if (listType === 'BulletList') return '•';
        const num = index + 1;
        if (language === 'bangla' || language === 'mixed') return `${toBanglaNumber(num)}.`;
        return `${num}.`;
    };

    const predefinedItems = parsePredefinedItems(description);

    const [items, setItems] = useState(() => {
        if (value?.items && value.items.length > 0) return value.items;
        return predefinedItems.length > 0 ? [...predefinedItems] : [];
    });

    const updateValue = (newItems) => {
        onChange({ items: newItems, listType });
    };

    const [initialSyncDone, setInitialSyncDone] = useState(false);

    useEffect(() => {
        if (value?.items) {
            if (value.items.length === 0) {
                setItems([]);
            } else if (JSON.stringify(value.items) !== JSON.stringify(items)) {
                setItems(value.items);
            }
        } else if (!initialSyncDone) {
            setItems([]);
        }
    }, [value?.items]);

    useEffect(() => {
        if (!initialSyncDone && (!value?.items || value.items.length === 0) && predefinedItems.length > 0) {
            setItems([...predefinedItems]);
            updateValue([...predefinedItems]);
            setInitialSyncDone(true);
        } else {
            setInitialSyncDone(true);
        }
    }, []);

    const addItem = () => {
        const newItems = [...items, ''];
        setItems(newItems);
        updateValue(newItems);
    };

    const removeItem = (index) => {
        const newItems = items.filter((_, i) => i !== index);
        setItems(newItems);
        updateValue(newItems);
    };

    const updateItem = (index, val) => {
        const newItems = items.map((item, i) => (i === index ? val : item));
        setItems(newItems);
        updateValue(newItems);
    };

    const markerWidth = listType === 'BulletList' ? 'min-w-[20px]' : 'min-w-[30px]';

    return (
        <div
            className="rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4"
            onFocus={onFocus}
        >
            <div className="flex flex-col gap-3">
                {/* Label */}
                <span className="font-medium text-sm text-gray-800 dark:text-gray-100">{label}</span>

                {/* Items */}
                <div className="flex flex-col gap-1.5">
                    {items.length === 0 ? (
                        <div className="flex items-center gap-2">
                            <span className={`text-sm font-medium text-gray-400 dark:text-gray-500 text-right ${markerWidth} shrink-0`}>
                                {getMarker(0)}
                            </span>
                            <span className="text-sm text-gray-400 dark:text-gray-500 flex-1">
                                {t('listBuilder.clickToAddRow') || 'Click + to add item'}
                            </span>
                        </div>
                    ) : (
                        items.map((item, index) => (
                            <div key={index} className="flex items-start gap-2">
                                <span className={`text-sm font-medium text-gray-400 dark:text-gray-500 text-right ${markerWidth} shrink-0 pt-2`}>
                                    {getMarker(index)}
                                </span>
                                <textarea
                                    value={item}
                                    onChange={(e) => updateItem(index, e.target.value)}
                                    rows={1}
                                    className="flex-1 resize-none rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent overflow-hidden min-h-[34px]"
                                    onInput={(e) => {
                                        e.target.style.height = 'auto';
                                        e.target.style.height = e.target.scrollHeight + 'px';
                                    }}
                                />
                                <button
                                    type="button"
                                    onClick={() => removeItem(index)}
                                    title={t('listBuilder.removeItem') || 'Remove item'}
                                    className="mt-1 p-1 rounded text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors shrink-0"
                                >
                                    <IconTrash size={14} />
                                </button>
                            </div>
                        ))
                    )}
                </div>

                {/* Add button */}
                <button
                    type="button"
                    onClick={addItem}
                    title={t('listBuilder.addItem') || 'Add item'}
                    className="ml-auto flex items-center justify-center w-6 h-6 rounded bg-green-500 hover:bg-green-600 text-white transition-colors shrink-0"
                >
                    <IconPlus size={14} />
                </button>
            </div>
        </div>
    );
}
